import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React, { createContext, PureComponent } from 'react';
import memoizeOne from 'memoize-one';
import noop from '@atlaskit/ds-lib/noop';
import Portal from '@atlaskit/portal';
import { layers } from '@atlaskit/theme/constants';
import Blanket from '../styled/blanket';
import { Fade } from './animation';
const {
  Consumer: TargetConsumer,
  Provider: TargetProvider
} = /*#__PURE__*/createContext(undefined);
const SpotlightContext = /*#__PURE__*/createContext({
  opened: noop,
  closed: noop,
  targets: {}
});
const {
  Consumer: SpotlightStateConsumer,
  Provider: SpotlightStateProvider
} = SpotlightContext;
export { TargetConsumer };
export { SpotlightContext, SpotlightStateConsumer as SpotlightConsumer };
const Container = ({
  component: Wrapper,
  children
}) => /*#__PURE__*/React.createElement(Wrapper, null, children);

/**
 * __Spotlight manager__
 *
 * A spotlight manager manages the visibility of spotlights used to introduce new features to users through focused messages or multi-step tours.
 *
 * - [Examples](https://atlassian.design/components/onboarding/examples)
 * - [Code](https://atlassian.design/components/onboarding/code)
 * - [Usage](https://atlassian.design/components/onboarding/usage)
 */
export default class SpotlightManager extends PureComponent {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      spotlightCount: 0,
      targets: {}
    });
    _defineProperty(this, "targetRef", name => element => {
      this.setState(state => ({
        targets: {
          ...state.targets,
          [name]: element || undefined
        }
      }));
    });
    _defineProperty(this, "spotlightOpen", () => {
      this.setState(state => ({
        spotlightCount: state.spotlightCount + 1
      }));
    });
    _defineProperty(this, "spotlightClose", () => {
      this.setState(state => ({
        spotlightCount: state.spotlightCount - 1
      }));
    });
    _defineProperty(this, "getStateProviderValue", memoizeOne(targets => ({
      opened: this.spotlightOpen,
      closed: this.spotlightClose,
      targets
    })));
  }
  componentDidMount() {
    if (typeof process !== 'undefined' && process.env.NODE_ENV !== 'production' && !process.env.CI) {
      if (this.props.component) {
        // eslint-disable-next-line no-console
        console.warn(`Atlaskit: The SpotlightManager 'component' prop is deprecated. Please wrap the SpotlightManager in the component instead.`);
      }
    }
  }
  render() {
    const {
      blanketIsTinted,
      children,
      component: Tag,
      onBlanketClicked
    } = this.props;
    return /*#__PURE__*/React.createElement(SpotlightStateProvider, {
      value: this.getStateProviderValue(this.state.targets)
    }, /*#__PURE__*/React.createElement(TargetProvider, {
      value: this.targetRef
    }, /*#__PURE__*/React.createElement(Container, {
      component: Tag || React.Fragment
    }, /*#__PURE__*/React.createElement(Fade, {
      hasEntered: this.state.spotlightCount > 0
    }, animationStyles => /*#__PURE__*/React.createElement(Portal, {
      zIndex: layers.spotlight()
    }, /*#__PURE__*/React.createElement(Blanket
    /* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */, {
      style: animationStyles,
      isTinted: blanketIsTinted,
      onBlanketClicked: onBlanketClicked
    }))), children)));
  }
}
_defineProperty(SpotlightManager, "defaultProps", {
  blanketIsTinted: true
});